import { zodResolver } from '@hookform/resolvers/zod';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Modal, ModalCloseButton, ModalOverlay, ModalPortal, ModalTitle } from '../../../../design-system/modal/modal';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';

const editCardNicknameSchema = z.object({
  displayName: z.string().min(1, 'Card nickname is required').max(50, 'Card nickname must be 50 characters or less'),
});

type EditCardNicknameFormData = z.infer<typeof editCardNicknameSchema>;

type EditCardNicknameDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (displayName: string) => Promise<void>;
  currentDisplayName: string;
  defaultNickname?: string;
  isLoading?: boolean;
};

export const EditCardNicknameDialog = ({
  isOpen,
  onClose,
  onSave,
  currentDisplayName,
  defaultNickname,
  isLoading = false,
}: EditCardNicknameDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditCardNicknameFormData>({
    resolver: zodResolver(editCardNicknameSchema),
    defaultValues: {
      displayName: currentDisplayName,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ displayName: currentDisplayName });
    }
  }, [isOpen, currentDisplayName, reset]);

  const onSubmit = async (data: EditCardNicknameFormData) => {
    await onSave(data.displayName);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const placeholder = defaultNickname || currentDisplayName;

  return (
    <Modal open={isOpen} onOpenChange={handleOpenChange}>
      <ModalPortal>
        <ModalOverlay className="bg-black/50" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-[50%] left-[50%] z-50 grid w-[425px] max-w-[calc(100vw-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-border bg-white p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
          aria-describedby={undefined}
        >
          <ModalCloseButton onClick={onClose} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack space={6} align="stretch">
              <ModalTitle className="text-lg leading-none font-bold text-foreground">Edit card nickname</ModalTitle>

              <div className="py-2">
                <input
                  type="text"
                  {...register('displayName')}
                  placeholder={placeholder}
                  className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-yellow-400 focus:ring-[3px] focus:ring-yellow-400/50 focus:outline-none"
                  aria-invalid={!!errors.displayName}
                />
                {errors.displayName && <p className="mt-1 text-sm text-destructive">{errors.displayName.message}</p>}
              </div>

              <Inline space={2} justify="end" className="w-full">
                <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>

                <Button type="submit" isLoading={isLoading} className="bg-yellow-400 text-black hover:bg-yellow-500">
                  Save
                </Button>
              </Inline>
            </Stack>
          </form>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
};
